import React from 'react';
import styled from '@emotion/styled';
import { Database, FileArchive, Users, Calendar, CheckCircle, AlertTriangle, Check, Search, X, List } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import StatusIcon from './StatusIcon';

// Loan Detail View Components
const LoanDetailContainer = styled.div`
  position: relative;
  max-width: 1200px;
  margin: 0 auto;
`;

const LoanDetailHeader = styled.div`
  padding: 1.5rem 1.5rem 0.75rem 1.5rem;
  margin-bottom: 1.5rem;
`;

const LoanDetailTitle = styled.h2`
  margin: 0 0 0.5rem 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: #111827;
  cursor: pointer;
  transition: color 0.2s;
  
  &:hover {
    color: #3b82f6;
  }
`;

const LoanDetailSubtitle = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 2rem;
  flex-wrap: wrap;
`;

// KPI section with search
const KPISection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  gap: 1rem;
  padding: 0 1.5rem;
`;

const KPIContainer = styled.div`
  display: flex;
  gap: 1rem;
`;

const KPICard = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  text-align: center;
  min-width: 80px;
`;

const KPIValue = styled.div`
  font-size: 1.25rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 0.25rem;
`;

const KPILabel = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const SearchContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-width: 400px;
`;

const SearchInputContainer = styled.div`
  position: relative;
  width: 100%;
`;

const SearchInput = styled.input`
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  width: 100%;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  pointer-events: none;
`;

const PresetFilters = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const PresetButton = styled.button`
  padding: 0.375rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  background: ${props => props.active ? '#f3f4f6' : 'white'};
  color: ${props => props.active ? '#374151' : '#6b7280'};
  font-size: 0.75rem;
  font-weight: ${props => props.active ? '600' : '500'};
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.active ? '#e5e7eb' : '#f9fafb'};
    border-color: ${props => props.active ? '#9ca3af' : '#9ca3af'};
  }
`;

const ReturnLink = styled.button`
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  background: none;
  border: none;
  color: #6b7280;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  
  &:hover {
    color: #374151;
  }
`;

const LoanDetailContent = styled.div`
  background: transparent;
`;

// Table components for split view
const TwoColumnSplit = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  padding: 0 1.5rem 1.5rem 1.5rem;
`;

const ColumnTable = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  background: white;
`;

const ColumnTableHeader = styled.div`
  display: grid;
  grid-template-columns: 0.3fr 2fr 2fr 1fr;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-bottom: 2px solid #e5e7eb;
  font-weight: 600;
  font-size: 0.75rem;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background: #f8fafc;
`;

const ColumnTableRow = styled.div`
  display: grid;
  grid-template-columns: 0.3fr 2fr 2fr 1fr;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #f3f4f6;
  align-items: center;
  
  &:hover {
    background: #f9fafb;
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const ColumnFieldName = styled.div`
  font-weight: 500;
  color: #374151;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  cursor: help;
`;

const ColumnFieldValue = styled.div`
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  cursor: help;
`;

const ColumnFieldSource = styled.div`
  font-size: 0.75rem;
  color: #9ca3af;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  cursor: help;
`;

// Data type icon component
const DataTypeIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  /* Themed background colors based on props.type */
  ${props => props.type === 'boolean' && `background: #f59e0b; color: white;`}
  ${props => props.type === 'date' && `background: #2563eb; color: white;`}
  ${props => props.type === 'currency' && `background: #f59e0b; color: white;`}
  ${props => props.type === 'percent' && `background: #7c3aed; color: white;`}
  ${props => props.type === 'number' && `background: #8b5cf6; color: white;`}
  ${props => props.type === 'phone' && `background: #10b981; color: white;`}
  ${props => props.type === 'email' && `background: #ef4444; color: white;`}
  ${props => props.type === 'address' && `background: #059669; color: white;`}
  ${props => props.type === 'id' && `background: #6b7280; color: white;`}
  ${props => props.type === 'text' && `background: #ec4899; color: white;`}
  ${props => props.type === 'balloon' && `background: #f97316; color: white;`}
`;

// Icon-only status indicator

// Helper function to detect data type from field name and value
const detectDataType = (fieldName, value) => {
  const name = fieldName.toLowerCase();
  const val = value ? value.toString().toLowerCase() : '';

  const booleanFieldNames = ['indicator', 'flag', 'enabled', 'disabled', 'active', 'inactive', 'required', 'optional'];
  const booleanValues = ['yes', 'no', 'true', 'false', 'y', 'n', '1', '0', 'on', 'off'];

  if (booleanFieldNames.some(pattern => name.includes(pattern)) || booleanValues.includes(val)) {
    return { type: 'boolean', icon: '🔄' };
  }
  if (name.includes('date') || name.includes('time')) { return { type: 'date', icon: '📅' }; }
  if (name.includes('amount') || name.includes('balance') || name.includes('price') || name.includes('cost')) { return { type: 'currency', icon: '💰' }; }
  if (name.includes('rate') || name.includes('percent') || name.includes('%')) { return { type: 'percent', icon: '📊' }; }
  if (name.includes('count') || name.includes('number') || name.includes('quantity')) { return { type: 'number', icon: '🔢' }; }
  if (name.includes('phone') || name.includes('fax')) { return { type: 'phone', icon: '📞' }; }
  if (name.includes('email')) { return { type: 'email', icon: '📧' }; }
  if (name.includes('address') || name.includes('street') || name.includes('city') || name.includes('state') || name.includes('zip')) { return { type: 'address', icon: '📍' }; }
  if (name.includes('ssn') || name.includes('tax') || name.includes('id')) { return { type: 'id', icon: '🆔' }; }
  if (name.includes('balloon')) { return { type: 'balloon', icon: '🎈' }; }
  return { type: 'text', icon: '📝' };
};

// Helper function to format field values
const formatFieldValue = (fieldName, value, status) => {
  if (status === 'not_found') {
    return 'NOT FOUND';
  }

  if (!value) {
    return 'No data';
  }

  // Handle boolean values
  if (['yes', 'no', 'true', 'false', 'y', 'n', '1', '0', 'on', 'off'].includes(value.toLowerCase())) {
    const isTrue = ['yes', 'true', 'y'].includes(value.toLowerCase());
    return (
      <span style={{ 
        color: isTrue ? '#059669' : '#dc2626',
        fontWeight: '600'
      }}>
        {isTrue ? <><Check size={12} /> YES</> : <><X size={12} /> NO</>}
      </span>
    );
  }
  
  return value;
};

// Main component
const LoanDetailView = ({ loanDetail, onClose, compact = false, splitView = false }) => {
  const [searchTerm, setSearchTerm] = React.useState('');
  const [activePreset, setActivePreset] = React.useState('all');
  const navigate = useNavigate();
  
  // Comprehensive null checking
  if (!loanDetail) {
    console.warn('LoanDetailView: loanDetail is null or undefined');
    return null;
  }
  
  if (!loanDetail.fields || !Array.isArray(loanDetail.fields)) {
    console.warn('LoanDetailView: loanDetail.fields is not an array', loanDetail);
    return null;
  }
  
  if (typeof onClose !== 'function') {
    console.warn('LoanDetailView: onClose is not a function', onClose);
    return null;
  }

  // Filter fields based on search term and preset
  const filteredFields = loanDetail.fields.filter(field => {
    // Apply preset filter first
    let passesPreset = true;
    if (activePreset === 'not_found') {
      passesPreset = field.status === 'not_found';
    } else if (activePreset === 'found') {
      passesPreset = field.status === 'found';
    }
    
    if (!passesPreset) return false;
    
    // Apply search term filter
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      field.field_name.toLowerCase().includes(searchLower) ||
      (field.field_value && field.field_value.toLowerCase().includes(searchLower)) ||
      (field.source_file && field.source_file.toLowerCase().includes(searchLower))
    );
  });

  const handlePresetClick = (preset) => {
    setActivePreset(preset);
    setSearchTerm(''); // Clear search when using presets
  };

  try {
    return (
    <LoanDetailContainer>
      <LoanDetailContent>
        <ReturnLink onClick={onClose}>
          <List size={16} style={{ marginRight: '0.5rem' }} />
          Return to List
        </ReturnLink>
        
        <LoanDetailHeader>
          <LoanDetailTitle onClick={() => navigate(`/document-analysis/${loanDetail.loan_id}`)}>
            Loan {loanDetail.loan_id || 'Unknown'}
          </LoanDetailTitle>
          <LoanDetailSubtitle>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Users size={16} />
              {(() => {
                // Extract borrower name from fields array
                const borrowerField = loanDetail.fields?.find(field => 
                  field.field_name === 'Borrower Name'
                );
                return borrowerField?.field_value || 'Unknown Borrower';
              })()}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Calendar size={16} />
              Updated {loanDetail.last_updated ? new Date(loanDetail.last_updated).toLocaleDateString() : 'Unknown'}
            </div>
          </LoanDetailSubtitle>
        </LoanDetailHeader>
        <KPISection>
          <KPIContainer>
            <KPICard>
              <KPIValue>{loanDetail.total_fields || 0}</KPIValue>
              <KPILabel>Total</KPILabel>
            </KPICard>
            <KPICard>
              <KPIValue>{loanDetail.found_fields || 0}</KPIValue>
              <KPILabel>Found</KPILabel>
            </KPICard>
            <KPICard>
              <KPIValue>{(loanDetail.total_fields || 0) - (loanDetail.found_fields || 0)}</KPIValue>
              <KPILabel>Missing</KPILabel>
            </KPICard>
            <KPICard>
              <KPIValue>{loanDetail.data_sources || 0}</KPIValue>
              <KPILabel>Sources</KPILabel>
            </KPICard>
          </KPIContainer>
          <SearchContainer>
            <SearchInputContainer>
              <SearchIcon>
                <Search size={16} />
              </SearchIcon>
              <SearchInput
                type="text"
                placeholder="Search fields, values, sources..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setActivePreset('all'); // Reset preset when typing
                }}
              />
            </SearchInputContainer>
            <PresetFilters>
              <PresetButton 
                active={activePreset === 'all'} 
                onClick={() => handlePresetClick('all')}
              >
                All Fields
              </PresetButton>
              <PresetButton 
                active={activePreset === 'found'} 
                onClick={() => handlePresetClick('found')}
              >
                Found Only
              </PresetButton>
              <PresetButton 
                active={activePreset === 'not_found'} 
                onClick={() => handlePresetClick('not_found')}
              >
                Not Found Only
              </PresetButton>
            </PresetFilters>
          </SearchContainer>
        </KPISection>
        
        {splitView ? (
          <TwoColumnSplit>
            {(() => {
              const midPoint = Math.ceil(filteredFields.length / 2);
              const leftFields = filteredFields.slice(0, midPoint);
              const rightFields = filteredFields.slice(midPoint);
              
              return (
                <>
                  <ColumnTable>
                    <ColumnTableHeader>
                      <div></div>
                      <div>Field Name</div>
                      <div>Value</div>
                      <div>Source</div>
                    </ColumnTableHeader>
                    {leftFields.map((field, index) => {
                      return (
                        <ColumnTableRow key={index}>
                          <StatusIcon status={field.status} />
                          <ColumnFieldName title={field.field_name}>
                            {field.field_name}
                          </ColumnFieldName>
                          <ColumnFieldValue title={field.field_value}>
                            {formatFieldValue(field.field_name, field.field_value, field.status)}
                          </ColumnFieldValue>
                          <ColumnFieldSource title={field.source_file}>
                            {field.source_file || 'N/A'}
                          </ColumnFieldSource>
                        </ColumnTableRow>
                      );
                    })}
                  </ColumnTable>
                  
                  <ColumnTable>
                    <ColumnTableHeader>
                      <div></div>
                      <div>Field Name</div>
                      <div>Value</div>
                      <div>Source</div>
                    </ColumnTableHeader>
                    {rightFields.map((field, index) => {
                      return (
                        <ColumnTableRow key={index}>
                          <StatusIcon status={field.status} />
                          <ColumnFieldName title={field.field_name}>
                            {field.field_name}
                          </ColumnFieldName>
                          <ColumnFieldValue title={field.field_value}>
                            {formatFieldValue(field.field_name, field.field_value, field.status)}
                          </ColumnFieldValue>
                          <ColumnFieldSource title={field.source_file}>
                            {field.source_file || 'N/A'}
                          </ColumnFieldSource>
                        </ColumnTableRow>
                      );
                    })}
                  </ColumnTable>
                </>
              );
            })()}
          </TwoColumnSplit>
        ) : (
          <div style={{ 
            padding: '0 1.5rem 1.5rem 1.5rem',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            overflow: 'hidden',
            background: 'white'
          }}>
            {/* Table Header */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '0.3fr 2fr 2fr 1fr',
              gap: '0.75rem',
              padding: '0.75rem 1rem',
              borderBottom: '2px solid #e5e7eb',
              fontWeight: '600',
              fontSize: '0.75rem',
              color: '#6b7280',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              background: '#f8fafc'
            }}>
              <div></div>
              <div>Field Name</div>
              <div>Value</div>
              <div>Source</div>
            </div>
            
            {filteredFields.map((field, index) => {
              return (
                <div key={index} style={{ 
                  display: 'grid', 
                  gridTemplateColumns: '0.3fr 2fr 2fr 1fr',
                  gap: '0.75rem',
                  padding: '0.75rem 1rem',
                  borderBottom: index === filteredFields.length - 1 ? 'none' : '1px solid #f3f4f6',
                  alignItems: 'center'
                }}>
                  <StatusIcon status={field.status} />
                  <div style={{ fontWeight: '500', color: '#374151' }}>
                    {field.field_name}
                  </div>
                  <div style={{ color: '#6b7280' }}>
                    {formatFieldValue(field.field_name, field.field_value, field.status)}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                    {field.source_file || 'N/A'}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </LoanDetailContent>
    </LoanDetailContainer>
    );
  } catch (error) {
    console.error('LoanDetailView rendering error:', error);
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: '#dc2626' }}>
        <h3>Error loading loan details</h3>
        <p>There was an error displaying the loan information. Please try again.</p>
        <button 
          onClick={onClose}
          style={{ 
            marginTop: '1rem', 
            padding: '0.5rem 1rem', 
            background: '#3b82f6', 
            color: 'white', 
            border: 'none', 
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Return to List
        </button>
      </div>
    );
  }
};

export default LoanDetailView;
